import { forwardRef, type PropsWithChildren, type ReactNode } from 'react'
import {
  isNotDefined,
  type PermissionDefinition,
  type PermissionListItem,
  type PermissionType,
  sortPermissionOptions,
  type WithPermissionEntityId,
} from '@terros/common'
import { showNotification } from '@mantine/notifications'
import { Group, RadioGroup, Stack, Switch, Text } from '@mantine/core'
import { useQueryDisclosure, useToastError } from '@hooks'
import { useProfile, useUpdatePermission } from '@front/model'
import { ConditionTooltip } from '../ConditionTooltip'
import { ButtonReset } from '../buttons/reset'
import { TButton } from '../buttons'
import {
  getInheritedScope,
  getPermissionActionDescription,
  getRadioGroupProps,
  isPermissionChanged,
  stringifyPermissionScopes,
} from './util'
import { type PermissionAllowedScope, type PermissionEditScope } from './types'
import { PermTreeModal } from './PermTreeModal'
import { PermissionRadioOption } from './PermissionRadioOption'
import { HistoryButton } from './HistoryButton'

type Props = WithPermissionEntityId & {
  permissionType: PermissionType
  permDefinition: PermissionDefinition
  info: PermissionListItem
  readOnly?: boolean
}

export const PermissionRadioGroup = ({
  permissionType,
  permDefinition,
  entityId,
  info,
  readOnly = false,
}: Props): ReactNode => {
  const { user } = useProfile()
  const [treeOpened, { open: openTree, close: closeTree }] = useQueryDisclosure(
    `scope-${entityId}-${permissionType}`,
    false
  )

  const { update, isUpdating, error } = useUpdatePermission()
  useToastError(error)

  const updatePermission = (scope: PermissionEditScope): void => {
    update({ entityId, permissionType, scope })
  }

  const { canEdit, isPermEnabled, isSwitchToggleable, switchOnly, availableEntities } = getRadioGroupProps(info)
  const isViewingSelf = isNotDefined(user) || entityId === user.userId
  const canUserEdit = canEdit && !isViewingSelf && !readOnly

  const handleEnable = (): void => {
    const newPermission = getLowestEnabledScope(info.overridable)
    if (!newPermission) {
      showNotification({
        message: 'Unable to enable permission',
        color: 'red',
      })
      return
    }
    updatePermission(newPermission)
  }

  const inheritedScope = getInheritedScope(info.inherits)

  const canReset = canUserEdit && isPermissionChanged(info, entityId)
  const resetTooltip = canReset ? `Reset to ${stringifyPermissionScopes(inheritedScope)}` : undefined
  const showOptions = isPermEnabled && !switchOnly

  return (
    <ConditionTooltip
      conditions={[
        {
          active: readOnly,
          label: 'Restore to edit these permissions',
        },
        {
          active: isViewingSelf,
          label: 'You cannot edit your own permissions',
        },
        {
          active: !canEdit,
          label: 'You do not have permission to edit this user',
        },
      ]}
    >
      <GroupItem id={permissionType}>
        <ButtonReset
          disabled={!canReset}
          loading={isUpdating}
          tooltip={resetTooltip}
          onClick={() => updatePermission('default')}
        />

        <Stack gap='xs'>
          <Text>{permDefinition.label}</Text>
          <div className='flex flex-row gap-4'>
            <Switch
              checked={isPermEnabled}
              disabled={!canUserEdit || !isSwitchToggleable}
              onChange={(e) => {
                if (!e.target.checked) {
                  updatePermission('none')
                } else {
                  handleEnable()
                }
              }}
            />
            <Stack gap='xs'>
              <Text size='sm' style={{ color: 'gray' }}>
                {getDescription(permDefinition, switchOnly, info, isPermEnabled)}
              </Text>
              {showOptions && (
                <RadioGroup>
                  <Stack gap='xs'>
                    {availableEntities.map((entity) => {
                      return (
                        <PermissionRadioOption
                          disabled={isUpdating || availableEntities.length <= 1 || !canUserEdit}
                          entity={entity}
                          key={entity}
                          optionEditInfo={info}
                          onSelect={() => updatePermission(entity)}
                        />
                      )
                    })}
                  </Stack>
                </RadioGroup>
              )}
            </Stack>
          </div>
        </Stack>

        <Group align='flex-start' gap='xs' wrap='nowrap' className='ml-auto'>
          <TButton
            aria-haspopup='dialog'
            buttonType='icon-rounded'
            icon='account_tree'
            tooltip='View permission sources'
            variant='secondary'
            onClick={openTree}
          />
          <HistoryButton permissionType={permissionType} permissionInfo={info} />
        </Group>
        <PermTreeModal
          definition={permDefinition}
          entityId={entityId}
          info={info}
          opened={treeOpened}
          permissionType={permissionType}
          readOnly={readOnly}
          onClose={closeTree}
        />
      </GroupItem>
    </ConditionTooltip>
  )
}

export const getDescription = (
  definition: PermissionDefinition,
  switchOnly: boolean,
  editInfo: PermissionListItem,
  enabled: boolean
): string => {
  const description = getPermissionActionDescription(definition)
  const enabledScopes = definition.entities.filter((e) => e !== 'none')
  if (enabledScopes.length === 1) return description

  const scope = enabled ? displayScope(editInfo, switchOnly) : '.'

  return description + scope
}

const displayScope = (editInfo: PermissionListItem, switchOnly: boolean): string => {
  const displayEntities = editInfo.primaryScope?.entity ?? 'none'

  return ' for' + (switchOnly ? ` ${stringifyPermissionScopes(displayEntities)}` : ': ')
}

const getLowestEnabledScope = (overridable: PermissionAllowedScope[]): PermissionEditScope | undefined => {
  const sorted = sortPermissionOptions(overridable).filter((p) => p !== 'none')
  return sorted.at(-1)
}

const GroupItem = forwardRef<
  HTMLDivElement,
  PropsWithChildren & {
    id: string
  }
>(({ id, ...props }, ref) => (
  <div ref={ref} {...props} className='flex flex-row gap-4' id={id}>
    {props.children}
  </div>
))

GroupItem.displayName = 'PermissionRadioGroupItem'
